/**
 * useAnimatedOverlay — drives the open/close animation cycle shared by
 * SidebarOverlay, CommandPalette, and UserDropdown.
 *
 * Cycle:
 *   1. `open` flips to true → mount immediately, then on the next animation
 *      frame flip `animateIn` to true (lets CSS transition from the
 *      enter state to the at-rest state).
 *   2. `open` flips to false → drop `animateIn` (transitions back to the
 *      enter state), then unmount after `duration` ms (the CSS transition
 *      duration).
 *
 * Returns `{ mounted, animateIn }`:
 *   - `mounted`     — whether the overlay should be in the DOM (the
 *                     post-exit-animation unmount gate).
 *   - `animateIn`   — whether the overlay is at rest (used to toggle the
 *                     transition target classes).
 *
 * Callers keep their own effects (keyed on `open` / `mounted`) for
 * focus capture/restore, body scroll-lock, and any state reset they
 * need when the overlay finishes closing. Those concerns are not part
 * of the animation cycle and stay in the consumer.
 */
import { useEffect, useState } from 'react'

export interface AnimatedOverlayState {
  mounted: boolean
  animateIn: boolean
}

export function useAnimatedOverlay(open: boolean, duration: number): AnimatedOverlayState {
  const [mounted, setMounted] = useState(false)
  const [animateIn, setAnimateIn] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      const raf = requestAnimationFrame(() => setAnimateIn(true))
      return () => cancelAnimationFrame(raf)
    }
    setAnimateIn(false)
    const timer = setTimeout(() => setMounted(false), duration)
    return () => clearTimeout(timer)
  }, [open, duration])

  return { mounted, animateIn }
}
